import { useEffect, useRef, useState } from "react";
import { Bot, Send, Plus, MessageSquare, Trash2 } from "lucide-react";
import { api } from "@/lib/api";
import { useToast } from "@/lib/toast";
import { PageHeader, Spinner } from "@/components/UI";

const uid = () =>
  crypto.randomUUID ? crypto.randomUUID() : "s-" + Math.random().toString(36).slice(2);

const STARTERS = [
  "Help me draft a bid response for a CCTV installation project",
  "What documents are required for a PPDA bid submission?",
  "Summarise the key requirements from this opportunity description…",
  "Suggest a pricing strategy for a cybersecurity assessment",
];

export default function Assistant() {
  const [sessionId, setSessionId] = useState(uid());
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [sessions, setSessions] = useState([]);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);
  const { toast } = useToast();

  const loadSessions = () => api.chatSessions().then(setSessions).catch(() => {});
  useEffect(() => { loadSessions(); }, []);
  useEffect(() => { scrollRef.current?.scrollTo({ top: 999999, behavior: "smooth" }); }, [messages, sending]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || sending) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: msg }]);
    setSending(true);
    try {
      const res = await api.chat(sessionId, msg);
      setMessages((m) => [...m, { role: "assistant", content: res.reply }]);
      loadSessions();
    } catch (e) {
      toast(e?.response?.data?.detail || "AI error", "error");
      setMessages((m) => m.slice(0, -1));
    } finally {
      setSending(false);
    }
  };

  const open = async (id) => {
    setSessionId(id);
    const s = await api.chatSession(id);
    setMessages((s.messages || []).map((m) => ({ role: m.role, content: m.content })));
  };
  const newChat = () => {
    setSessionId(uid());
    setMessages([]);
    setInput("");
    toast("New chat started — ask anything below", "success");
    // Focus the input so the user immediately sees "something happened"
    setTimeout(() => inputRef.current?.focus(), 50);
  };
  const del = async (id) => {
    await api.deleteChatSession(id);
    loadSessions();
    if (id === sessionId) newChat();
  };

  return (
    <div data-testid="page-assistant">
      <PageHeader
        icon={Bot}
        title="AI Assistant"
        subtitle="Gemini 3 Pro · multi-turn memory · bid-aware"
      />
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-5">
        <div className="space-y-3">
          <button data-testid="new-chat" onClick={newChat} className="btn btn-primary w-full justify-center">
            <Plus size={14} /> New chat
          </button>
          <div className="card card-tight max-h-[480px] overflow-y-auto">
            {sessions.length === 0 ? (
              <div className="text-xs muted p-3">No previous chats</div>
            ) : (
              sessions.map((s) => (
                <div
                  key={s.id}
                  data-testid={`session-${s.id}`}
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg cursor-pointer text-sm group ${
                    s.id === sessionId ? "bg-[var(--green-50)]" : "hover:bg-slate-50"
                  }`}
                  style={s.id === sessionId ? { color: "var(--green-700)" } : {}}
                >
                  <MessageSquare size={14} className="flex-shrink-0" />
                  <button onClick={() => open(s.id)} className="flex-1 min-w-0 text-left">
                    <div className="truncate text-xs">{s.preview || "New chat"}</div>
                  </button>
                  <button
                    data-testid={`del-session-${s.id}`}
                    onClick={() => del(s.id)}
                    className="opacity-0 group-hover:opacity-100 text-slate-400 hover:text-red-600"
                  >
                    <Trash2 size={12} />
                  </button>
                </div>
              ))
            )}
          </div>
        </div>

        <div className="lg:col-span-3 card flex flex-col h-[620px]">
          <div ref={scrollRef} className="flex-1 overflow-y-auto p-5 space-y-4" data-testid="msgs">
            {messages.length === 0 && (
              <div className="text-center py-10">
                <div className="empty-icon mx-auto">
                  <Bot size={22} />
                </div>
                <p className="font-semibold mb-1">How can I help you today?</p>
                <p className="text-xs muted mb-5">Ask anything about bids, proposals or procurement</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-w-xl mx-auto">
                  {STARTERS.map((s) => (
                    <button
                      key={s}
                      data-testid={`starter-${s.slice(0, 24).replace(/\s+/g, "-").toLowerCase()}`}
                      onClick={() => send(s)}
                      className="text-left text-xs p-3 border border-slate-100 rounded-lg hover:border-[#0e6939] hover:bg-[var(--green-50)] transition"
                      style={{ color: "var(--ink-700)" }}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {messages.map((m, i) => (
              // Chat messages are strictly append-only, so index is stable
              // for the lifetime of the array. Composite key includes role + i.
              <div key={`msg-${m.role}-${i}`} data-testid={`msg-${i}`} className={`flex gap-3 ${m.role === "user" ? "justify-end" : ""}`}>
                {m.role === "assistant" && (
                  <div className="w-7 h-7 rounded-full bg-[var(--green-50)] flex items-center justify-center flex-shrink-0">
                    <Bot size={14} style={{ color: "var(--green-700)" }} />
                  </div>
                )}
                <div
                  className={`max-w-[75%] rounded-2xl px-4 py-2.5 text-sm whitespace-pre-wrap leading-relaxed ${
                    m.role === "user" ? "text-white" : "bg-slate-100"
                  }`}
                  style={m.role === "user" ? { background: "var(--green-700)" } : {}}
                >
                  {m.content}
                </div>
              </div>
            ))}

            {sending && (
              <div className="flex gap-3" data-testid="typing">
                <div className="w-7 h-7 rounded-full bg-[var(--green-50)] flex items-center justify-center">
                  <Bot size={14} style={{ color: "var(--green-700)" }} />
                </div>
                <div className="bg-slate-100 rounded-2xl px-4 py-3 text-sm muted flex items-center gap-2">
                  <Spinner /> Thinking…
                </div>
              </div>
            )}
          </div>

          <form
            onSubmit={(e) => { e.preventDefault(); send(); }}
            className="border-t border-slate-100 p-3 flex gap-2"
          >
            <input
              ref={inputRef}
              data-testid="chat-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask anything about bids or procurement…"
              className="input flex-1"
              disabled={sending}
            />
            <button
              data-testid="chat-send"
              type="submit"
              disabled={sending || !input.trim()}
              className="btn btn-primary"
            >
              <Send size={14} />
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
